import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, CalendarCheck, DollarSign,
  Settings, Church, X, BookOpen, Phone, Mail,
} from "lucide-react";
import { useChurch } from "@/hooks/useChurch";
import { useSidebarState } from "@/hooks/useSidebarState";
import { usePermissions } from "@/hooks/usePermissions";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import { useEffect } from "react";

export default function AppSidebar() {
  const { mobileOpen, setMobileOpen } = useSidebarState();
  const location = useLocation();
  const navigate = useNavigate();
  const sidebarGradient = "linear-gradient(160deg, #1e3a8a 0%, #1e3a8a 100%)";

  const { profile } = useChurch();
  const { role } = useAuth();
  const { isChurchAdmin, hasAccess, permissionsLoading } = usePermissions();

  // Close mobile sidebar on navigation
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) {
      return;
    }

    const scrollY = window.scrollY;
    const originalHtmlOverflow = document.documentElement.style.overflow;
    const originalBodyOverflow = document.body.style.overflow;
    const originalBodyPosition = document.body.style.position;
    const originalBodyTop = document.body.style.top;
    const originalBodyWidth = document.body.style.width;

    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";

    return () => {
      document.documentElement.style.overflow = originalHtmlOverflow;
      document.body.style.overflow = originalBodyOverflow;
      document.body.style.position = originalBodyPosition;
      document.body.style.top = originalBodyTop;
      document.body.style.width = originalBodyWidth;
      window.scrollTo(0, scrollY);
    };
  }, [mobileOpen]);

  // Build nav items dynamically from permissions — hidden completely if no access
  const navItems = [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", show: true },
    { to: "/people", icon: Users, label: "People", show: permissionsLoading || isChurchAdmin || hasAccess("members") },
    {
      to: "/finance", icon: DollarSign, label: "Finance",
      show: permissionsLoading || isChurchAdmin || hasAccess("finance"),
    },
    { to: "/events", icon: CalendarCheck, label: "Events", show: permissionsLoading || isChurchAdmin || hasAccess("events") },
    { to: "/communication", icon: BookOpen, label: "Comms", show: permissionsLoading || isChurchAdmin || hasAccess("communication") },
    {
      to: "/ministries", icon: Church, label: "Ministries",
      show: permissionsLoading || isChurchAdmin || hasAccess("ministries"),
    },
    {
      to: "/settings", icon: Settings, label: "Settings",
      show: permissionsLoading || isChurchAdmin || hasAccess("settings"),
    },
  ];

  const visibleNavItems = navItems.filter((item) => item.show);
  const hasOnlyDashboardAccess = !permissionsLoading && visibleNavItems.length === 1 && visibleNavItems[0]?.to === "/dashboard";
  const mobileDrawerPrimaryItems = visibleNavItems.filter((item) => item.to !== "/settings");
  const mobileDrawerSecondaryItems = visibleNavItems.filter((item) => item.to === "/settings");

  // Compact icon-only nav for desktop
  const compactNav = (
    <>
      {/* Logo + Acronym */}
      <div className="flex flex-col items-center pt-4 pb-2 cursor-pointer flex-shrink-0" onClick={() => navigate("/dashboard")}>
        {profile?.logo_url ? (
          <img src={profile.logo_url} alt="Church logo" className="w-9 h-9 rounded-xl object-cover" />
        ) : (
          <img src="/ichurch-logo.svg" alt="iChurch Data logo" className="w-9 h-9 rounded-xl object-cover" />
        )}
        {profile?.abbreviation && (
          <span className="text-[9px] font-bold text-white/80 mt-1 tracking-wide truncate max-w-[64px] text-center">
            {profile.abbreviation}
          </span>
        )}
      </div>

      {/* Nav */}
      <nav className="flex flex-col items-center gap-1 px-1.5 py-2">
        {visibleNavItems.map((item) => {
            const isMinistryRoute = item.to === "/ministries" && (location.pathname.startsWith("/ministries") || location.pathname.startsWith("/organizations"));
            const isActive = item.to === "/dashboard"
              ? location.pathname === "/dashboard"
              : isMinistryRoute || location.pathname.startsWith(item.to);
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={cn(
                  "flex flex-col items-center gap-0.5 w-full py-2 rounded-xl text-[11px] font-medium transition-colors duration-150",
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-white/70 hover:bg-white/10 hover:text-white"
                )}
                title={item.label}
              >
                <item.icon className="w-5 h-5" />
                <span className="leading-tight truncate max-w-full px-0.5">{item.label}</span>
              </NavLink>
            );
          })}
      </nav>
      {hasOnlyDashboardAccess && (
        <div className="px-2 pb-3 text-center text-[9px] text-white/70 leading-tight">
          Limited role access
        </div>
      )}
    </>
  );

  // Expanded mobile nav
  const mobileNav = (
    <>
      <div className="relative border-b border-white/10 bg-[#17356f] px-4 py-3 overflow-hidden cursor-pointer" onClick={() => navigate("/dashboard")}>
        <div className="flex items-center gap-2.5 pl-2.5 pr-10">
          {profile?.logo_url ? (
            <img src={profile.logo_url} alt="Church logo" className="h-10 w-10 rounded-2xl object-cover flex-shrink-0" loading="lazy" decoding="async" />
          ) : (
            <img src="/ichurch-logo.svg" alt="iChurch Data logo" className="h-10 w-10 rounded-2xl object-cover flex-shrink-0" loading="lazy" decoding="async" />
          )}
          <span className="font-heading text-[1.05rem] font-bold text-sidebar-foreground tracking-tight truncate">
            {profile?.abbreviation || profile?.church_name || "iChurch Data"}
          </span>
        </div>
        {(profile?.phone || profile?.email || profile?.bible_verse) && (
          <div className="mt-3 rounded-xl border border-white/10 bg-white/10 px-3 py-2.5 text-white/85">
            <div className="space-y-1.5">
              {profile?.phone && (
                <div className="flex items-start gap-2 text-[12px] leading-snug">
                  <Phone className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-white/70" />
                  <span>{profile.phone}</span>
                </div>
              )}
              {profile?.email && (
                <div className="flex items-start gap-2 text-[12px] leading-snug break-all">
                  <Mail className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-white/70" />
                  <span>{profile.email}</span>
                </div>
              )}
              {profile?.bible_verse && (
                <div className="pt-0.5">
                  <div className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-white/60">
                    <BookOpen className="h-3 w-3" />
                    <span>Verse of the Year</span>
                  </div>
                  <blockquote className="text-[12px] italic leading-snug text-white/90">
                    &ldquo;{profile.bible_verse}&rdquo;
                  </blockquote>
                  {profile?.bible_reference && (
                    <p className="mt-1 text-[10px] font-semibold text-white/70">— {profile.bible_reference}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
        <button onClick={() => setMobileOpen(false)} className="absolute right-2.5 top-3 flex h-9 w-9 items-center justify-center rounded-full active:bg-white/10">
          <X className="h-4.5 w-4.5 text-sidebar-foreground" />
        </button>
      </div>
      <div className="flex min-h-0 flex-1 flex-col">
      <nav className="flex-1 overflow-y-auto px-3 py-3.5">
        <div className="space-y-0.5">
        {mobileDrawerPrimaryItems.map((item) => {
            const isMinistryRoute = item.to === "/ministries" && (location.pathname.startsWith("/ministries") || location.pathname.startsWith("/organizations"));
            const isActive = item.to === "/dashboard"
              ? location.pathname === "/dashboard"
              : isMinistryRoute || location.pathname.startsWith(item.to);
            return (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setMobileOpen(false)}
                className={cn(
                  "relative flex min-h-[2.6rem] items-center gap-2.5 rounded-xl px-3.5 py-2 text-[0.92rem] font-semibold transition-colors duration-150",
                  isActive
                    ? "bg-white/16 text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]"
                    : "text-white/85 active:bg-white/10"
                )}
              >
                {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-r-full bg-white/90" />}
                <item.icon className="h-[1.05rem] w-[1.05rem] flex-shrink-0" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </div>

        {mobileDrawerSecondaryItems.length > 0 && (
          <div className="mt-4 border-t border-white/10 pt-3.5">
            <div className="space-y-0.5">
              {mobileDrawerSecondaryItems.map((item) => {
                const isActive = location.pathname.startsWith(item.to);
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={() => setMobileOpen(false)}
                    className={cn(
                      "relative flex min-h-[2.6rem] items-center gap-2.5 rounded-xl px-3.5 py-2 text-[0.92rem] font-semibold transition-colors duration-150",
                      isActive
                        ? "bg-white/16 text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]"
                        : "text-white/85 active:bg-white/10"
                    )}
                  >
                    {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-r-full bg-white/90" />}
                    <item.icon className="h-[1.05rem] w-[1.05rem] flex-shrink-0" />
                    <span>{item.label}</span>
                  </NavLink>
                );
              })}
            </div>
          </div>
        )}

        {hasOnlyDashboardAccess && (
          <div className="mt-4 rounded-lg bg-white/10 px-4 py-3 text-sm text-white/80">
            Your role currently has Dashboard-only access.
          </div>
        )}

      </nav>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop sidebar – compact floating icon bar */}
      <aside
        className="fixed left-10 top-1/2 -translate-y-1/2 z-40 w-[80px] max-h-[calc(100vh-8rem)] text-sidebar-foreground flex-col transition-all duration-300 hidden md:flex rounded-2xl shadow-lg overflow-hidden"
        style={{ background: sidebarGradient }}
      >
        <div className="flex flex-col overflow-y-auto">
          {compactNav}
        </div>
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden" onClick={() => setMobileOpen(false)}>
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" />
          <aside
            className="fixed inset-y-0 left-0 h-screen max-h-screen w-[min(19rem,86vw)] text-sidebar-foreground flex flex-col shadow-xl overflow-hidden rounded-r-3xl animate-in slide-in-from-left duration-300"
            style={{ background: sidebarGradient }}
            onClick={(e) => e.stopPropagation()}
          >
            {mobileNav}
          </aside>
        </div>
      )}
    </>
  );
}
